/* eslint-disable max-lines-per-function */
/*eslint complexity: ["error", 45]*/
import React from 'react';

import { useTranslation } from '../../../common/hooks/useTranslation';
import {
  GetDictionaryDto,
  GetUmpPositionDto,
  GetUmpPositionDtoPositionTypeEnum,
  ProfileSchemaDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { GridColumn } from '../../../common/components/Grid/GridDataTypes';
import { Grid } from '../../../common/components/Grid/Grid';
import momencik from '../../../common/momencik';
import numberFormatter from '../../../common/numberFormatter';
import mathUtils from '../../../utils/mathUtils';
import { PositionTypeObj } from '../../../common/pokCore/validation/enums';

import { fieldWithError } from './UmpEstimatesGrid';
import {
  UmpNewPathPhase,
  UmpNewSpecialistTeam,
  UmpParameterMapperForm,
} from './UmpParameterMapperForm';

interface UmpPositionsListProps {
  data: GetUmpPositionDto[];
  selectedKeys?: string[];
  isPurchaseSameAsSales?: boolean;
  profileSchema: ProfileSchemaDto;
  companyId?: string;
  pathPhaseDictionaries?: GetDictionaryDto[];
  pathPhaseEnabled?: boolean;
  updateParams?: (
    uid: string,
    newPathPhaseDict: UmpNewPathPhase | undefined,
    newSpecialistTeam: UmpNewSpecialistTeam | undefined,
  ) => void;
  onRowClick?: (key: string) => Promise<void> | void | undefined;
}

const hasEditableParams = (
  item: GetUmpPositionDto,
  pathPhaseEnabled?: boolean,
  pathPhaseDictionaries?: GetDictionaryDto[],
): boolean =>
  (!!pathPhaseEnabled && (pathPhaseDictionaries?.length || 0) > 0) ||
  !!item.positionNamePok;

const getUmpPositionTeamName = (item: GetUmpPositionDto) =>
  item.specialistTeamName || item.deliveryTeamName || '';

export const UmpPositionsGrid: React.FC<UmpPositionsListProps> = props => {
  const { t, tk } = useTranslation('administrationLng');
  const columns: GridColumn[] = [
    { header: t(tk.umpImport.positionNameUmp), property: 'positionName' },
    {
      header: t(tk.umpImport.positionNamePok),
      property: 'positionNamePok',
    },
    { header: t(tk.umpImport.positionType), property: 'positionType' },
    { header: t(tk.common.countryMarket), property: 'countryMarket' },
    ...(props.pathPhaseEnabled
      ? [{ header: t(tk.common.positionPhase), property: 'pathPhaseDict' }]
      : []),
    { header: '', property: 'changeButton', noSorting: true },
    { header: t(tk.common.deliveryTeam), property: 'specialistTeam' },
    { header: t(tk.common.startDate), property: 'startDate' },
    { header: t(tk.common.endDate), property: 'endDate' },
    { header: t(tk.administration.purchaseForm), property: 'purchaseForm' },
    { header: t(tk.administration.format), property: 'format' },
    ...(props.profileSchema.ownName
      ? [
          {
            header: t(tk.administration.qualifiedMedium),
            property: 'qualifiedMedium',
          },
        ]
      : []),
    ...(props.profileSchema.salesNet
      ? [
          {
            header: t(tk.administration.salesNet),
            property: 'salesNet',
          },
        ]
      : []),
    ...(props.profileSchema.rcSalesTotal
      ? [
          {
            header: t(tk.administration.rcSalesTotal),
            property: 'rcSalesTotal',
          },
        ]
      : []),
    ...(props.profileSchema.salesDiscountPercent
      ? [
          {
            header: t(tk.administration.salesDiscountPercent),
            property: 'salesDiscountPercent',
          },
        ]
      : []),
    ...(props.profileSchema.salesCommissionAmount
      ? [
          {
            header: t(tk.administration.salesCommissionAmount),
            property: 'salesCommissionAmount',
          },
        ]
      : []),
    ...(props.profileSchema.salesBeerTax
      ? [
          {
            header: t(tk.administration.salesBeerTax),
            property: 'salesBeerTax',
          },
        ]
      : []),
    {
      header: t(tk.administration.purchaseNetTotal),
      property: 'purchaseNetTotal',
    },
  ];

  const data = props.data.map(item => {
    const calculatePurchaseNetTotal: string | null = (() => {
      if (item.positionType === GetUmpPositionDtoPositionTypeEnum.Commission) {
        return null;
      }

      if (!props.isPurchaseSameAsSales) {
        return '0';
      }

      if (
        item.positionType ===
        GetUmpPositionDtoPositionTypeEnum.TransferredCommission
      ) {
        return item.salesCommissionAmount ?? null;
      }

      //TODO: zmienic na podejscie uniwersalne
      const isInternet_tvp = props.profileSchema.rcSalesTotal?.required;
      if (isInternet_tvp) {
        return item.rcSalesTotal ?? '0';
      }
      if (props.profileSchema.salesBeerTax) {
        return mathUtils
          .add(item.salesNet ?? '0', item.salesBeerTax ?? '0')
          .toString();
      }
      return item.salesNet ?? '0';
    })();

    return {
      key: item.uid,
      values: {
        positionName: fieldWithError(
          item.positionName,
          item.errors?.positionName,
        ),
        positionNamePok: fieldWithError(
          item.positionNamePok || '',
          item.errors?.positionNamePok,
        ),
        positionType: item.positionType
          ? t(PositionTypeObj[item.positionType])
          : '',
        countryMarket: fieldWithError(
          item.countryMarket?.value || '',
          item.errors?.countryMarket,
        ),
        pathPhaseDict: fieldWithError(
          item.pathPhaseDict?.value || '',
          item.errors?.pathPhaseDict,
        ),
        specialistTeam: fieldWithError(
          getUmpPositionTeamName(item),
          item.errors?.specialistTeam,
        ),
        changeButton: hasEditableParams(
          item,
          props.pathPhaseEnabled,
          props.pathPhaseDictionaries,
        ) ? (
          <UmpParameterMapperForm
            umpPosition={item}
            companyId={props.companyId}
            pathPhaseDictionaries={props.pathPhaseDictionaries}
            pathPhaseEnabled={props.pathPhaseEnabled}
            handleUpdate={props.updateParams}
          />
        ) : (
          ''
        ),
        startDate: fieldWithError(
          momencik(item.startDate),
          item.errors?.startDate,
        ),
        endDate: fieldWithError(momencik(item.endDate), item.errors?.endDate),
        purchaseForm: fieldWithError(
          item.purchaseForm?.value || '',
          item.errors?.purchaseForm,
        ),
        format: fieldWithError(item.format?.value || '', item.errors?.format),
        qualifiedMedium: fieldWithError(
          item.qualifiedMedium || '',
          item.errors?.qualifiedMedium,
        ),
        salesNet: numberFormatter(item.salesNet || null, true),
        rcSalesTotal: numberFormatter(item.rcSalesTotal || null, true),
        salesDiscountPercent: item.salesDiscountPercent
          ? `${item.salesDiscountPercent}%`
          : '',
        salesBeerTax: numberFormatter(item.salesBeerTax || null, true),
        salesCommissionAmount: numberFormatter(
          item.salesCommissionAmount || null,
          true,
        ),
        purchaseNetTotal: numberFormatter(calculatePurchaseNetTotal, true),
      },
      tdCssClass: {
        salesNet: 'amounts-align-right',
        rcSalesTotal: 'amounts-align-right',
        salesDiscountPercent: 'amounts-align-right',
        salesCommissionAmount: 'amounts-align-right',
        purchaseNetTotal: 'amounts-align-right',
      },
      orderByStringValues: {
        positionName: item.positionName || '',
        positionNamePok: item.positionNamePok || '',
        countryMarket: item.countryMarket?.value || '',
        pathPhaseDict: item.pathPhaseDict?.value || '',
        specialistTeam: getUmpPositionTeamName(item),
        startDate: momencik(item.startDate),
        endDate: momencik(item.endDate),
        purchaseForm: item.purchaseForm?.value || '',
        format: item.format?.value || '',
        qualifiedMedium: item.qualifiedMedium || '',
      },
      orderByNumberValues: {
        salesNet: mathUtils.parse(item.salesNet),
        rcSalesTotal: mathUtils.parse(item.rcSalesTotal),
        salesCommissionAmount: mathUtils.parse(item.salesCommissionAmount),
        purchaseNetTotal: mathUtils.parse(
          props.isPurchaseSameAsSales ? item.rcSalesTotal : 0,
        ),
        salesDiscountPercent: mathUtils.parse(item.salesDiscountPercent),
      },
      rowCssClass:
        item.errors && Object.keys(item.errors).length > 0 ? 'error-row' : '',
    };
  });

  return (
    <Grid
      data={data}
      columns={columns}
      showFilter={true}
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage={t(tk.common.noPositions)}
      enableMultiSelect={true}
      selectedKeys={props.selectedKeys}
      onRowClick={props.onRowClick}
    />
  );
};
